/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

import keyBy from 'lodash/fp/keyBy.js';
import React from 'react';
import { componentQuestions } from '@hbrisk/sp3-risk-model-support/models/componentQuestions/index.js';
import {
  ALLOW_COMPONENTS_TO_AFFECT_STRUCTURAL_PROPERTIES,
  ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT,
  ALLOW_TEMPORARY_REPAIRS,
  ALLOW_TEMPORARY_SHORING,
  BUILDING_ASPECT_RATIO,
  BUILDING_MODS_COLUMN_PIER_SPANDREL_RATIO,
  BUILDING_MODS_COLUMN_RATIO_LESS_THAN_TYPICAL,
  BUILDING_MODS_CONSTRUCTION_QUALITY,
  BUILDING_MODS_CROSS_GRAIN_OOP_CONN,
  BUILDING_MODS_DIAPHRAGM_DEFICIENCY,
  BUILDING_MODS_DIAPHRAM_OPEN,
  BUILDING_MODS_FLAT_PLATE_AS_BEAM,
  BUILDING_MODS_FULL_STORY_GRADE,
  BUILDING_MODS_FULL_STORY_GRADE_WLF,
  BUILDING_MODS_INTERIOR_WALL_DENSITY,
  BUILDING_MODS_K_BRACING,
  BUILDING_MODS_LIFT_SLAB_CONSTRUCTION,
  BUILDING_MODS_MATERIAL_DETERIORATION,
  BUILDING_MODS_NON_PARALLEL,
  BUILDING_MODS_NON_STRUCTURAL_CONSTRUCTION_QUALITY,
  BUILDING_MODS_OOP_OFFSET,
  BUILDING_MODS_OTHER_PLAN_IRREGULARITY,
  BUILDING_MODS_OTHER_VERTICAL_IRREGULARITY,
  BUILDING_MODS_PANEL_ZONE_DEFICIENCY,
  BUILDING_MODS_POUNDING_DIFFERENT_BUILDING_HEIGHTS,
  BUILDING_MODS_POUNDING_END_OF_BLOCK,
  BUILDING_MODS_POUNDING_FLOORS_UNALIGNED,
  BUILDING_MODS_REDUNDANCY,
  BUILDING_MODS_REENTRANT,
  BUILDING_MODS_SETBACK_INBOARD,
  BUILDING_MODS_SETBACK_IN_PLANE_LFRS,
  BUILDING_MODS_SETBACK_OUTBOARD,
  BUILDING_MODS_SPLIT_LEVEL,
  BUILDING_MODS_STRONG_COLUMN_WEAK_BEAM,
  BUILDING_MODS_WELD_STRENGTH,
  BUILDING_REPLACEMENT_TIME,
  BUILDING_SQUARE_FOOTAGE,
  BUILDING_VALUE_MODIFIER,
  CASH_ON_HAND,
  COLLAPSE_CAPACITY_BETA,
  COLLAPSE_CAPACITY_MEDIAN,
  COMPONENT_IMPORTANCE_FACTOR,
  COMPONENT_POPULATION_METHOD,
  COMPONENT_SOFT_COST_MODIFIER,
  CONCRETE_SHEAR_WALLS_LOAD_BEARING,
  COST_PER_SQ_FT,
  DAMAGE_CORRELATION_MODEL,
  DATE_COST_MULTIPLIER,
  DESIGN_CODE_YEAR,
  EQUIPMENT_PREQUALIFIED,
  FEMA_154_SCORE,
  FEMA_154_SCORE_CALCULATION_METHOD,
  GROUND_MOTION_METHOD,
  HAZARD_EDITION,
  INCLUDE_COLLAPSE,
  INCLUDE_DESIGN_INTENSITIES,
  INCLUDE_IMPEDANCE_LONG_LEAD_TIME,
  INCLUDE_PER_REALIZATION_OUTPUT,
  INCLUDE_RESIDUAL_DRIFT,
  INCLUDE_SURGE_DEMAND,
  INCLUDE_TIME_BASED_ANALYSIS,
  INCLUDE_USRC,
  IS_DENSE_URBAN_AREA,
  MASONRY_SHEAR_WALLS_LOAD_BEARING,
  MAX_WALKABLE_STORY,
  MINIMUM_NUMBER_OF_EXITS_REQUIRED,
  MITIGATION_CONTRACTOR,
  MITIGATION_ENGINEER,
  MITIGATION_FUNDING_SOURCE,
  MITIGATION_INSPECTOR,
  MODEL_NAME,
  MODE_SHAPE_METHOD,
  NEED_ELEVATOR_FOR_FUNCTION,
  NON_STRUCTURAL_RETROFIT_CODE_YEAR,
  NON_STRUCTURAL_RETROFIT_YEAR,
  NUMBER_OF_STORIES,
  NUMBER_OF_SUBGRADE_STORIES,
  NUM_REALIZATIONS,
  OCCUPANCY,
  OCCUPANCY_COST_MULTIPLIER,
  PERCENT_GLAZING,
  PERCENT_OF_EXITS_REQUIRED,
  PERCENT_OF_PARTITIONS_ARE_FIRE_BRAKE,
  PERFORMANCE_GROUP_SERVICE_LOCATIONS,
  PLAN_TORSION,
  PREQUALIFIED_ACC_GROUND,
  PREQUALIFIED_ACC_ROOF,
  PREQUALIFIED_DESIGN_CODE_YEAR,
  PROB_COLLAPSE_MCE,
  REGION_COST_MULTIPLIER,
  REPAIR_COSTS_SCALE_WITH_BUILDING_VALUE,
  REPAIR_TIME_METHOD,
  RESIDUAL_DRIFT_SIMULATION_METHOD,
  RETURN_PERIODS,
  RETURN_PERIOD_DE,
  RETURN_PERIOD_MCE,
  RISK_CATEGORY,
  SEISMICITY_DESIGN,
  SEISMICITY_SHAKING,
  SEISMIC_IMPORTANCE_FACTOR,
  SITE_CLASS,
  SR_MODELING_DISPERSION,
  SR_RECORD_TO_RECORD_DISPERSION_MODEL,
  STRUCTURAL_RESPONSE_DERIVED_FROM,
  STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS,
  STRUCTURAL_RETROFIT_CODE_YEAR,
  STRUCTURAL_RETROFIT_YEAR,
  TRUNCATE_COLLAPSE_DISTRIBUTION,
  TRUNCATE_COLLAPSE_DISTRIBUTION_LEVEL,
  TRUNCATE_COMPONENT_DISTRIBUTION,
  TRUNCATE_COMPONENT_DISTRIBUTION_LEVEL,
  USE_SMART_EQUIPMENT_COMPONENTS,
  USE_SMART_PIPING_COMPONENTS,
  USE_UPDATED_ELEVATOR_COMPONENTS,
  VERTICAL_IRREGULARITY,
  YEAR_OF_CONSTRUCTION,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import SeismicityLevelLi from './SeismicityLevelLi/index.jsx';
import {
  storiesImage,
  floorsImage,
  locationsImage,
  chordRotationImage,
  couplingBeamRotationImage,
} from './index.module.scss';

const compQuestionTooltips = componentQuestions.reduce((acc, curr) => {
  const { name, tooltip } = curr;
  return [
    ...acc,
    tooltip
      ? {
          id: name,
          header: '',
          body: tooltip,
        }
      : {},
  ];
}, []);

const tips = [
  ...compQuestionTooltips,
  {
    id: ALLOW_COMPONENTS_TO_AFFECT_STRUCTURAL_PROPERTIES,
    header: 'Allow Components to Affect Structural Properties',
    body: (
      <div>
        <p>
          Allows wood light frame components to affect the default values of base shear strength and
          stiffness (period). Interior and exterior wood wall components, both structural and
          nonstructural, are used to determine the default strength and stiffness of the building.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Global strength and stiffness defaulting</li>
        </ul>
        <b>Note:</b>
        <p>
          Exterior wood wall components include both full height and perforated wall segments. The
          full height portions of the exterior walls are used to determine the walls’ contribution
          to the strength and stiffness of the building. The full height portion is the product of
          the component length and the full height exterior wall fraction (specified in the Building
          Layout page).
        </p>
        <p>
          The net effect of adding or removing structural components will vary from one building to
          the next, because buildings with fewer shear walls tend to have tighter nailing patterns,
          which means more strength and stiffness per foot of shear wall.
        </p>
        <b>Default:</b>
        <p>
          Building strength and stiffness defaults are determined based on the default wood light
          frame component inventory.
        </p>
      </div>
    ),
  },
  {
    id: ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT,
    header: 'Allow Fire Watch to be Sufficient for Occupancy',
    body: (
      <p>
        Turn this on if the building&apos;s jurisdiction allows for occupancy with a fire watch if
        the building&apos;s fire suppression system is compromised.
      </p>
    ),
  },
  {
    id: ALLOW_TEMPORARY_REPAIRS,
    header: 'Allow Temporary Repairs to Regain Occupancy/Function',
    body: (
      <p>
        Where possible, allow temporary measures to be taken immediately after the earthquake
        (before the full and permanent repairs are carried out) to mitigate
        reoccupancy/functionality issues.
      </p>
    ),
  },
  {
    id: ALLOW_TEMPORARY_SHORING,
    header: 'Allow Temporary Shoring to Regain Occupancy/Function',
    body: (
      <p>
        Where possible, allow temporary shoring to be installed immediately after the earthquake
        (before the full and permanent repairs are carried out) to mitigate
        reoccupancy/functionality issues.
      </p>
    ),
  },
  {
    id: 'annualRepairCost',
    header: 'Annual Repair Cost',
    body: (
      <div>
        <p>The average value of loss, per year, over a period of many years (FEMA P-58).</p>
      </div>
    ),
  },
  {
    id: 'atc138Output',
    header: 'ATC-138 Recovery Time (Beta) (with selected impeding factors)',
    body: (
      <p>
        The recovery times shown here include the impeding factors that are selected by the user.
      </p>
    ),
  },
  {
    id: 'averageAnnualLossBreakdown',
    header: 'Average Annual Loss Breakdown',
    body: (
      <p>
        The average annual loss is the average value of loss, per year, over a period of many years
        (FEMA P-58).
      </p>
    ),
  },
  {
    id: 'baseShearStrength',
    header: 'Base shear strength',
    body: (
      <div>
        <p>
          The ultimate base shear capacity, incorporating both overstrength and non-structural
          contributions. This is not the design base shear.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural response prediction</li>
          <li>Collapse capacity defaulting (for some systems)</li>
        </ul>
        <b>Defaults:</b>
        <p className="pl-3">
          Based on structural system, design code, level of seismicity of site, and building
          fundamental period.
        </p>
      </div>
    ),
  },
  {
    id: 'brbToDiaphragmRatio',
    header: 'BRB/Diaphragm Strength Ratio',
    body: (
      <div>
        <p>Ratio of the BRB design strength to the design strength of the diaphragm.</p>
        <p>This is essentially the ratio of design Cs values (or ratio of R values).</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural response prediction</li>
          <li>Global strength and stiffness defaulting</li>
          <li>Collapse capacity defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: BUILDING_ASPECT_RATIO,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Automated selection of structural components</li>
          <li>Automated selection of non-structural components</li>
          <li>Global strength and stiffness defaulting (wind design, tilt-up diaphragm design)</li>
          <li>Structural response prediction</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Most buildings are assumed to be square in plan with the exception of Wood Light Frame
          structures.
        </p>
      </div>
    ),
  },
  {
    id: 'buildingComponentsChecklist',
    header: 'Building Components Checklist',
    body: (
      <div>
        <p>
          SP3 algorithms default the components likely to be in the building, based on code,
          occupancy, and structural system. Non-structural component capacities are also defaulted
          based on design code. When provided, responses to these checklist questions are
          incorporated into the SP3 algorithms for defaulting the building components model.
        </p>
      </div>
    ),
  },
  {
    id: 'buildingRepairTime',
    header: 'Building Recovery Time',
    body: (
      <div>
        <p>
          Time to repair the damaged building to meet a specified recovery level (i.e. re-occupancy,
          functional recovery, full recovery).
        </p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_FULL_STORY_GRADE_WLF,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No sloping site.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_FULL_STORY_GRADE,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No sloping site.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_SETBACK_OUTBOARD,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No setback.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_SETBACK_INBOARD,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No setback.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_SETBACK_IN_PLANE_LFRS,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No setback.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_COLUMN_RATIO_LESS_THAN_TYPICAL,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No short column/pier.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_COLUMN_PIER_SPANDREL_RATIO,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No short column/pier.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_SPLIT_LEVEL,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No split level.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_OTHER_VERTICAL_IRREGULARITY,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No other vertical irregularities.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_NON_PARALLEL,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No non-parallel system.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_REENTRANT,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No re-entrant corners.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_DIAPHRAGM_DEFICIENCY,
    header: 'Diaphragm Deficiency',
    body: (
      <div>
        <p>
          There is at least a full story grade change from one side of the building to the other.
        </p>
        <ul>
          <li>None: No diaphragm deficiency</li>
          <li>
            Moderate: Defficient diaphragm strength or out of plane wall connection strength. Most
            common in older flexible diaphragms.
          </li>
          <li>
            Severe: Severe out-of-plan wall connection strength (e.g. wood leger in cross-grain
            bending)
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_DIAPHRAM_OPEN,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No diaphragm openings.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_OOP_OFFSET,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No misaligned beams and columns.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_OTHER_PLAN_IRREGULARITY,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No other plan irregularity.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_PANEL_ZONE_DEFICIENCY,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
          <li>Building ultimate strength defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_POUNDING_DIFFERENT_BUILDING_HEIGHTS,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_POUNDING_END_OF_BLOCK,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_POUNDING_FLOORS_UNALIGNED,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_REDUNDANCY,
    body: (
      <div>
        <p>Many buildings meet the requirements for redundancy.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No - System is not redundant.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_K_BRACING,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Strength and stiffness defaulting</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No K bracing.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_LIFT_SLAB_CONSTRUCTION,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
          <li>Building ultimate strength defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_FLAT_PLATE_AS_BEAM,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Strength and stiffness defaulting</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No flat plate beams as lateral system.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_CROSS_GRAIN_OOP_CONN,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Depends on building era.</p>
        <b>Notes:</b>
        <p>This will have no effect on buildings UBC 1997 or later.</p>
        <p>
          For component defaulting of roof-to-wall ties (i.e. out of plane wall to diaphragm
          connections), use the building component checklist page
        </p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_INTERIOR_WALL_DENSITY,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No - interior walls are not densely spaced.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_CONSTRUCTION_QUALITY,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Strength and Stiffness defaulting</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Average.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_NON_STRUCTURAL_CONSTRUCTION_QUALITY,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Computed capacities of non-structural components</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Average.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_MATERIAL_DETERIORATION,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Global strength and stiffness defaulting</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">None.</p>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_STRONG_COLUMN_WEAK_BEAM,
    body: (
      <div>
        <b>Options:</b>
        <ul>
          <li>
            Deficient: Strong column weak beam ratio (SCWB) worse that typical, corresponding to
            SCWB &lt; 0.7 (note, this is appoximatly equivilent to a SCWB &lt; 0.6 according to
            modern AISC design provisions, which use nominal properties in the numerator and
            expected properties in the denominator).
          </li>
          <li>
            Average: Strong column weak beam ratio it typical for pre-Northridge construction
            (between 0.7 and 1.25).
          </li>
          <li>
            Superior: Strong column weak beam ratio meets modern AISC requirements (SCWB &gt; 1.25)
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
          <li>Building ultimate strength defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: BUILDING_MODS_WELD_STRENGTH,
    body: (
      <div>
        <b>Options:</b>
        <ul>
          <li>
            Deficient: Higher than typical fracture potential. Roughly based on CVN = 7 to 8 ft-lb.
          </li>
          <li>
            Average: Typical fracture potential for pre-Northridge steel moment frames (CVN = 12
            ft-lb)
          </li>
          <li>
            Superior: Low fracture potential relative to typical pre-Northridge steel moment frames.
            Roughly based on CVN of 15 ft-lb or more. Note, this should be limited to cases without
            strong column weak beam deficiency (columns may control mechanism).
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity defaulting</li>
          <li>Building ultimate strength defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: BUILDING_REPLACEMENT_TIME,
    header: 'Building Replacement Time',
    body: (
      <div>
        <p>Total time to replace the building if it is damaged beyond repair.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Building downtime</li>
        </ul>
      </div>
    ),
  },
  {
    id: BUILDING_SQUARE_FOOTAGE,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Automated selection of structural components</li>
          <li>Automated selection of non-structural components</li>
          <li>Default building replacement value</li>
          <li>Expected overstrength/additional stiffness defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Most buildings are assumed to be 10,000 square feet per story with the exception of Wood
          Light Frame and Rigid Wall Flexible Diaphragm structures.
        </p>
      </div>
    ),
  },
  {
    id: 'buildingStability',
    header: 'Building Stability',
    body: (
      <div>
        <p>
          Parameters describing the collapse capacity of the building. The collapse capacity is
          modeled as a lognormal distribution and the default values are based on the FEMA 154
          collapse assessment methodology.
        </p>
        <p>
          If the MCE is included as a shaking intensity, the FEMA P-154 probability of collapse is
          anchored to that shaking intensity. Otherwise the FEMA P-154 probability of collapse is
          anchored at the modern design code MCE spectra.
        </p>
      </div>
    ),
  },
  {
    id: BUILDING_VALUE_MODIFIER,
    header: 'Building Value Modifier',
    body: (
      <div>
        <p>
          This modifier scales component costs so that their repair costs relative to the value of
          the building is consistent with expected values, based on SP3 defaults.
        </p>
        <b>Example:</b>
        <p>
          If the user-defined Total Cost per Square Foot is double the SP3 default Total Cost per
          Square Foot, then a factor of 2.0 is applied to component repair costs.
        </p>
        <b>Note:</b>
        <p>
          Additional cost modifiers for component groups, which compound with this effect, can be
          specified in the building components page.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Building value modifier applied to component repair costs.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'calculatedMedianCapacity',
    header: 'Calculated Median Capacity',
    body: (
      <div>
        <p>This is a designed component, so there is no standard capacity.</p>
        <p>
          The capacity is calculated according to the building code at the time of construction.
        </p>
      </div>
    ),
  },
  {
    id: 'capacityScaleFactor',
    header: 'Capacity Scale Factor',
    body: (
      <div>
        <p>
          Scale factor on the capacity of the component; e.g. for a component with median capacity
          of 1g, a 1.5 factor would increase the median capacity to 1.5g.
        </p>
        <b>Default:</b>
        <p className="pl-3">Default scale factor is 1.</p>
      </div>
    ),
  },
  {
    id: CASH_ON_HAND,
    header: 'Cash on Hand',
    body: (
      <div>
        <p>
          How much capital is available for financing in the event of earthquake shaking damage. If
          the cost of damage is less than the available capital, then financing will not delay the
          start of repairs (other impedance factors may still apply). The entered value is the
          percent of building replacement cost.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Downtime</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'cbr',
    header: 'Coupling Beam Rotation',
    body: (
      <div>
        <p>
          Peak coupling beam rotation (see figure, ASCE 41-13) at each story and principal direction
          of the building.
        </p>
        <b>Unit</b>
        <p>rad</p>
        <b>Dimension</b>
        <p>Story</p>
        <b>Diagram:</b>
        <div className="pt-3">
          <div className={couplingBeamRotationImage} />
        </div>
      </div>
    ),
  },
  {
    id: COLLAPSE_CAPACITY_BETA,
    header: 'Collapse Capacity Beta',
    body: (
      <div>
        <p>The uncertainty parameter of the collapse capacity lognormal distribution.</p>
        <b>Default:</b>
        <p className="pl-3">Based on FEMA P-154.</p>
      </div>
    ),
  },
  {
    id: COLLAPSE_CAPACITY_MEDIAN,
    header: 'Median Collapse Capacity',
    body: (
      <div>
        <p>
          Spectral acceleration at the fundamental period of the building at which the building has
          a 50% probability of collapse (the mean of the lognormal distribution).
        </p>
        <b>Default:</b>
        <p className="pl-3">Based on P[col|MCE] calculated using FEMA P-154.</p>
      </div>
    ),
  },
  {
    id: 'componentCostMultiplier',
    header: 'Cost Modifier',
    body: (
      <div>
        <p>
          Cost consequences modifier (per component group). A value of 1.0 means that the cost of a
          component group relative to the building value is “typical”.
        </p>
        <p>Example: A building has high end marble partition finishes.</p>
        <p>
          Recommendation: Adjust the cost modifier to Very High for Partition Walls component group.
        </p>
      </div>
    ),
  },
  {
    id: 'componentCapacityMultiplier',
    header: 'Capacity Modifier',
    body: (
      <div>
        <p>Multiplier on the damage threshold of components (per component group).</p>
        <p>
          Example: The glazing on a building was detailed to accomodate story drifts two times the
          code requirement.
        </p>
        <p>
          Recommendation: Apply a capacity multiplier of 2 to the Exterior Finishes component group.
        </p>
      </div>
    ),
  },
  {
    id: 'componentGroupHeaderCostModifier',
    header: 'Cost Modifier',
    body: (
      <div>
        <p>
          Cost consequences modifier (per component group). A value of 1.0 means that the cost of a
          component group relative to the building value is “typical”.
        </p>
      </div>
    ),
  },
  {
    id: 'componentGroupHeaderMaximumRepairPotential',
    header: 'Maximum Repair Potential',
    body: (
      <div>
        <p>
          Cost to repair all components in a component group from their highest damage state. This
          value includes volume discounting as well as all of the cost scaling factors shown on this
          page (e.g. Region Cost Multiplier). Individual component cost modifiers that are manually
          input in in the custom components page (available in the Advance Risk Model only) are also
          included.
        </p>
        <p>Note: maximum repair potential is not necessarily equal to component value</p>
      </div>
    ),
  },
  {
    id: 'componentGroupHeaderPercentOfBuildingReplacementValue',
    header: 'Percent of Building Replacement Value',
    body: (
      <div>
        <p>Maximum repair potential relative to building replacement value.</p>
        <p>
          Note: the summation of maximum repair potential for all components may be more or less
          than the replacement cost of the building, depending on how many of a building&lsquo;s
          components are dameagable in an earthquake and if their maximum repair potential is less
          than or greater than the cost to install them new.
        </p>
      </div>
    ),
  },
  {
    id: COMPONENT_IMPORTANCE_FACTOR,
    header: (
      <span>
        Component Importance Factor, I<small>p</small>
      </span>
    ),
    body: (
      <div>
        <p>
          Multiplier on the design force for components and their anchorage. Changing this factor
          will not affect the risk category. This will only affect building codes UBC 1976 and
          later.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Automated selection of non-structural components</li>
          <li>Computed capacities of non-structural components</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Based on design code, assigned risk category, and building component.
        </p>
      </div>
    ),
  },
  {
    id: COMPONENT_POPULATION_METHOD,
    header: 'Component Selection Method',
    body: (
      <div>
        <p>Specifies method to choose structural and non-structural components for your model</p>
        <b>Options</b>
        <ul>
          <li>
            SP3 Automated: Let the SP3 algorithms default the components likely to be in the
            building, based on code, occupancy, and structural system. Non-structural component
            capacities are also defaulted based on design code. The Building Components Table
            accounts for user inputs from other sections and will adjust as user-inputs are revised
            and the model is updated.
          </li>
          <li>
            User Defined: User-specified building components; this can be used to make small changes
            to defaulted components, or to create a full custom list.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: 'componentPopulationMethodCustomWarning',
    body: (
      <div>
        <p>
          When User Defined is selected, the Building Components Table is no longer automatically
          updated to reflect inputs from other sections.
        </p>
        <b>For example</b>
        <p>
          When User Defined is selected, changing the building square footage will no longer update
          component quantities.
        </p>
        <b>Warning</b>
        <p>
          Switching to SP3 Automated and updating your model will re-populate component data (all
          previously user defined component data will be lost).
        </p>
      </div>
    ),
  },
  {
    id: COMPONENT_SOFT_COST_MODIFIER,
    header: 'Component Soft Cost Modifier',
    body: (
      <div>
        <p>
          Multiplier on component repair costs to incorporate soft costs, such as design and
          permitting.
        </p>
        <b>Impact of Risk Assessment:</b>
        <p>
          Increases component repair costs. Does not affect building replacement costs (replacement
          cost values already include soft costs)
        </p>
        <b>Default:</b>
        <p>1.3, i.e. soft costs increase the total repair cost by 30%.</p>
      </div>
    ),
  },
  {
    id: CONCRETE_SHEAR_WALLS_LOAD_BEARING,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ui>
          <li>Red tagging</li>
        </ui>
      </div>
    ),
  },
  {
    id: 'confirmationCode',
    header: 'Confirmation Code',
    body: 'This is the code we sent to your email address',
  },
  {
    id: 'consequenceType',
    header: 'Consequence Type',
    body: (
      <div>
        <b>Options</b>
        <ul>
          <li>
            Sequential: Damage stated must occur in sequential order, with one state occurring
            before another is possible (see FEMA P-58 Vol. 1 Section 7.5.1)
          </li>
          <li>
            Mutually Exclusive: The occurrence of one damage state precludes the occurrence of
            another damage state (see FEMA P-58 Vol. 1 Section 7.5.2)
          </li>
          <li>
            Simultaneous: A series of potential damage states that are independent and unrelated in
            that may or may not occur at the same time (see FEMA P-58 Vol. 1 Section 7.5.3)
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: 'correlation',
    header: 'Correlation',
    body: (
      <div>
        <p>
          Determines if the components on the same floor/story for a given performance group will
          damage together or can damage separately.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>
            Correlated: within a given performance group, all the components on the same floor/story
            will damage together.
          </li>
          <li>
            Uncorrelated: within a given performance group, some components can be damaged while
            others are undamaged.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: COST_PER_SQ_FT,
    body: (
      <div>
        <p>This value is pre-populated based on region, occupancy, and building square footage.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Building replacement value (denominator of loss ratio)</li>
          <li>
            If component repair costs are scaled to be consistent with this value, then Total Cost
            per Square Foot will not affect the loss ratio.
          </li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Value based on RSmeans, region cost multiplier, and date cost multiplier
        </p>
      </div>
    ),
  },
  {
    id: 'costScaleFactor',
    header: 'Cost Scale Factor',
    body: (
      <div>
        <p>
          Scale factor to apply to the component repair cost consequences; e.g. a scale factor of
          1.5 would increase the component repair cost by 50%.
        </p>
        <b>Default:</b>
        <p className="pl-3">Default scale factor is 1.</p>
      </div>
    ),
  },
  {
    id: 'copySmartComponent',
    header: 'Copy Smart Component',
    body: (
      <div>
        <p>Smart Components are not currently allowed to be copied.</p>
      </div>
    ),
  },
  {
    id: 'cr',
    header: 'Chord Rotation',
    body: (
      <div>
        <p>
          Peak chord rotation (see figure, ASCE 41-13) at each story and principal direction of the
          building
        </p>
        <b>Unit</b>
        <p>rad</p>
        <b>Dimension</b>
        <p>Story</p>
        <b>Diagram:</b>
        <div className="pt-3">
          <div className={chordRotationImage} />
        </div>
      </div>
    ),
  },
  {
    id: 'createCustomComponentReference',
    header: "Can't find the component you are looking for?",
    body: (
      <div>
        <p>
          Create your own component by copying one from the{' '}
          <a href="/components">Component Database</a>.
        </p>
      </div>
    ),
  },
  {
    id: DAMAGE_CORRELATION_MODEL,
    header: 'Statistical Damage Correlation Model',
    body: (
      <div>
        <p>
          This is the correlation model used when simulating damage to individual building
          components when doing the FEMA P-58 Monte Carlo simulation.
        </p>
        <p>
          The ATC-138 updated method implements a partial correlation model based on the location in
          the building and level of similarity between components.
        </p>
        <b>Impact of Risk Assessment:</b>
        <ul>
          <li>Red tag probability</li>
          <li>Recovery time</li>
          <li>Loss variance</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          The Original FEMA P-58 model (although we anticipate this to change in the near future)
        </p>
      </div>
    ),
  },
  {
    id: DATE_COST_MULTIPLIER,
    header: 'Date Cost Multiplier',
    body: (
      <div>
        <p>Cost multiplier to convert 2011 dollars to present value dollars</p>
        <b>Impact of Risk Assessment:</b>
        <ul>
          <li>Defaults component repair costs.</li>
        </ul>
      </div>
    ),
  },
  {
    id: STRUCTURAL_RESPONSE_DERIVED_FROM,
    header: 'Derived From',
    body: (
      <div>
        The SP3 SRPE automation of this structural response is dependent on a peak interstory drift
        ratio; specify which interstory drift ratio to base this response on here.
      </div>
    ),
  },
  {
    id: DESIGN_CODE_YEAR,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Design code parameters</li>
          <li>Automated selection of structural components</li>
          <li>Automated selection of non-structural components</li>
          <li>Computed capacities of non-structural components</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Note:</b>
        <p>
          If &quot;ASCE 2022/ASCE 2016 (non-struct/struct)&quot; is selected, SP3 automations of
          nonstructural component capacities are based on ASCE 7-22 requirements, but SP3 automation
          of structural properties are based on ASCE 7-16 requirements.
        </p>
      </div>
    ),
  },
  {
    id: 'designCodeYearInt',
    header: 'Equivalent Design Code',
    body: (
      <div>
        <p>
          For international sites, an equivalent US design code must be specified to approximate how
          the building is designed. The parameters used in this code (i.e. base shear requirements)
          are determined by the Level of Seismicity.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Design code parameters</li>
          <li>Automated selection of structural components</li>
          <li>Automated selection of non-structural components</li>
          <li>Computed capacities of non-structural components</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Note:</b>
        <p>
          If &quot;ASCE 2022/ASCE 2016 (non-struct/struct)&quot; is selected, SP3 automations of
          nonstructural component capacities are based on ASCE 7-22 requirements, but SP3 automation
          of structural properties are based on ASCE 7-16 requirements.
        </p>
        <p>
          <strong>Knowledge Base:</strong>{' '}
          <a
            target="blank"
            href="https://support.sp3risk.com/space/KnowledgeBase/2203779073/Modeling+a+Building+at+an+International+Site"
          >
            Modeling a Building at an International Site
          </a>
        </p>
      </div>
    ),
  },
  {
    id: 'designPeriod1',
    header: (
      <span>
        Design Structural Model Period (Design T<sub>1</sub>)
      </span>
    ),
    body: (
      <div>
        <p>
          Fundamental (translational) period in each direction, computed from the structural model
          that is used to design the building; i.e. the period from an eigen analysis - not Ta,
          CuTa, or other code approximation. This period accounts for the lateral force resisting
          system only and does not include additional sources of stiffness that are normally ignored
          in design, such as stiffness from nonstructural components.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>
            Modal periods (T
            <sub>1</sub>, T<sub>2</sub>, T<sub>3</sub>)
          </li>
        </ul>
        <b>Default:</b>
        <p>Based on structural system, building height, design code, and level of seismicity.</p>
      </div>
    ),
  },
  {
    id: 'designSpectralAcceleration',
    header: 'Design Spectral Acceleration Values',
    body: (
      <div>
        <p>
          Spectral values defining the ASCE 7 two-point design spectrum. These are used to default
          building strength and stiffness properties and design forces on nonstructural components,
          where applicable.
        </p>
        <b>Impact on Risk Assessment:</b>
        <p>
          Global strength and stiffness defaulting and some nonstructural component capacities (for
          components with calculated capacities).
        </p>
        <b>Note:</b>
        <p>
          These values DO NOT affect the site hazard that is used to analyze the building, which is
          shown on the &quot;Soil and Ground Motion Info&quot; page.
        </p>
      </div>
    ),
  },
  {
    id: 'detailLevel',
    header: 'Level Of Detailing',
    body: (
      <div>
        <p>
          The level of detailing specified by the building code (or modern equivalent to level of
          detailing if not part of the building code used in design).
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Automated selection of structural components</li>
          <li>Design code parameters</li>
          <li>Structural response prediction (ductile vs. brittle response)</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Level of detailing based on the year of construction and seismicity of the site.
        </p>
      </div>
    ),
  },
  {
    id: 'diaph_duct',
    header: 'Diaphragm Ductility',
    body: (
      <div>
        <p>
          Peak diaphragm ductility (ratio of diaphragm deflection demand to the deflection at which
          the diaphragm yields) demand at each floor and principal direction of the building.
        </p>
        <b>Unit</b>
        <p>ratio</p>
        <b>Dimension</b>
        <p>Story</p>
      </div>
    ),
  },
  {
    id: 'driftLimit',
    header: 'Drift Design Limit',
    body: (
      <div>
        <p>
          The maximum allowable drift for the design level forces (per Code design process for
          controlling Code). Changing this factor will not affect the risk category.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Global strength and stiffness defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Based on assigned Risk Category and Building Type</p>
      </div>
    ),
  },
  {
    id: EQUIPMENT_PREQUALIFIED,
    header: 'Equipment Prequalified',
    body: (
      <div>
        <p>
          Prequalified equipment is tested to a specified shaking acceleration by the manufacturer
          to verify that it is reliable up to that level of shaking. If this option is selected, the
          strength of equipment will be based on a given level of prequalification acceleration
          (i.e., the acceleration level for which the equipment is prequalified). The level of
          acceleration that is assumed for component prequalified may be determined based on minimum
          building code requirements or user-defined acceleration values. Prequalified equipment
          fragilities adjust median and dispersion such that there is a low probability of damage at
          the qualification acceleration (i.e., prequalification acceleration is not the median
          capacity of the equipment).
        </p>
        <b>Impact on Risk Assessment:</b>
        <ui>
          <li>
            SP3 will auto-populate prequalified equipment fragilities, the strengths of which are
            based on an assumed level of acceleration for equipment prequalification tests, which is
            either determined by the governing building code or is user-defined.
          </li>
        </ui>
      </div>
    ),
  },
  {
    id: 'factorsDelayingTheStartOfRepairs',
    header: 'Factors Delaying the Start of Repairs',
    body: (
      <div>
        <p>
          Various factors influencing the time from the occurrence of the earthquake to when repairs
          on the building commence. All selected factors will be considered in the downtime
          assessment of the building model.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Building Downtime</li>
        </ul>
      </div>
    ),
  },
  {
    id: FEMA_154_SCORE,
    header: 'FEMA P-154 Score',
    body: (
      <div>
        <p>
          This is the score used to calculate the probability of collapse at the MCE level shaking.
          A higher score results in a lower probability of collapse. See FEMA P-154: Rapid Visual
          Screening of Buildings for Potential Seismic Hazards, available&nbsp;
          <a href="https://www.fema.gov/sites/default/files/2020-07/fema_earthquakes_rapid-visual-screening-of-buildings-for-potential-seismic-hazards-a-handbook-third-edition-fema-p-154.pdf">
            here
          </a>
          &nbsp;and the supporting documentation in FEMA P-155, available&nbsp;
          <a href="https://www.fema.gov/sites/default/files/2020-07/fema_earthquakes_rapid-visual-screening-of-buildings-for-potential-seismic-hazards-supporting-documentation-third-edition-fema-p-155.pdf">
            here
          </a>
          .
        </p>
        <p>
          With default FEMA P-154 scores, SP3 adds additional modifiers when converting the FEMA
          P-154 score to collapse probability for some cases. If the FEMA P-154 score is
          user-defined, the additional modifications are NOT applied. Cases that SP3 modifies are:
        </p>
        <ul>
          <li>Wood light frame buildings</li>
          <li>Moment frames that use Simpson Strong-Tie Yield Link Connections</li>
          <li>Tilt-ups with BRB internal lines of resistance</li>
          <li>Buildings with importance factors higher than the building code requirement</li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity</li>
        </ul>
      </div>
    ),
  },
  {
    id: FEMA_154_SCORE_CALCULATION_METHOD,
    header: 'FEMA P-154 Score Calculation Method',
    body: (
      <div>
        <b>Class-Based:</b>
        <ul>
          <li>
            <b>FEMA P-154 Rapid Visual Screening Checklist:</b> Determines the FEMA P-154 score
            using the checklist approach found in the FEMA P-154 data collection forms
            (available&nbsp;
            <a href="https://www.fema.gov/sites/default/files/2020-07/fema_earthquakes_rapid-visual-screening-of-buildings-for-potential-seismic-hazards-a-handbook-third-edition-fema-p-154.pdf">
              here
            </a>
            ), based on specified structural system type, building age, soil type, irregularities,
            and risk category.
          </li>
        </ul>
        <b>FEMA P-155 with Building-Specific Strength and Stiffness:</b>
        <ul>
          <li>
            <b>Standard FEMA P-155 Calculation Process:</b> Computes the collapse score directly
            with the method documented in FEMA P-155, but using the building-specific strength and
            period as inputs.
          </li>
          <li>
            <b>Calibrated for Consistency with FEMA P-695 Results (beta):</b> Calibrated by HB-Risk
            Engineers to produce results consistent with published FEMA P695 studies, based on the
            building specific strength and stiffness of the SP3 model.
          </li>
          <li>
            <b>Calibrated for Mean-Centered Collapse Capacity Estimation (beta):</b> SP3&apos;s best
            approximation of realistic collapse resistance. It is calibrated with the FEMA P-695
            method, but with increases in collapse resistance to account for recognized
            conservatisms in the FEMA P-695 methodology.
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Default FEMA P-154 Score</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Class based FEMA P-154 Rapid Visual Screening Checklist</p>
      </div>
    ),
  },
  {
    id: 'fittedSUL',
    header: 'Fitted SUL',
    body: (
      <div>
        <p>
          Fitted value of Scenario Upper Loss (SUL) - there is a 90% probability that the repair
          cost will not exceed this value.
        </p>
      </div>
    ),
  },
  {
    id: 'floor',
    header: 'Floors',
    body: (
      <div>
        <p>
          Floors can be expressed as a comma separated list of values (G, 2, 3, 5, 6, R) or ranges
          (G-3, 5-R).
        </p>
        <b>Note:</b>
        <ul>
          <li>
            Subgrade floors are denoted with a &quot;B&quot; prefix and count from the base level
            down.
          </li>
          <li>G denotes Ground Floor</li>
          <li>R denotes Roof</li>
        </ul>
        <b>Example ranges include:</b>
        <ul>
          <li>B2-R (basement level 2 through roof)</li>
          <li>B2-3 (basement level 2 through superstructure level 3)</li>
          <li>B2,B1 (basement levels 2 and 1)</li>
          <li>B3-B1 (basement levels 3 through 1)</li>
          <li>B3-R (basement levels 3 through roof)</li>
          <li>G-R (ground through roof)</li>
        </ul>
        <b>Example 3-Story Building</b>
        <div className="pt-3">
          <div className={floorsImage} />
        </div>
      </div>
    ),
  },
  {
    id: 'fullHeightExteriorWallFraction',
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Global strength and stiffness defaulting</li>
        </ul>
        <b>Tip:</b>
        <p>
          Exterior wood wall component fragilities include window and door openings, so the length
          of full height wall available for computing strength and stiffness is the product of the
          component length and the fraction that is full height.
        </p>
        <b>Example:</b>
        <p>
          My building has a 200&#8216; of exterior wall in direction 1. I have specified 140&#8216;
          of structural exterior wood wall components and 60&#8216; of nonstructural exterior wood
          wall components in direction 1. If the full height exterior wall fraction is 0.6, then SP3
          will assume 140&#8216; * 0.6 = 84&#8216; of full height exterior structural shear wall and
          60&#8216; * 0.6 = 36&#8216; of full height exterior nonstructural wall when determining
          the strength and stiffness of the building.
        </p>
      </div>
    ),
  },
  {
    id: 'fullRecovery',
    header: 'Full Recovery',
    body: (
      <div>
        <p>Time to repair the damaged building to its pre-earthquake state.</p>
      </div>
    ),
  },
  {
    id: 'functionalRecovery',
    header: 'Functional Recovery',
    body: (
      <div>
        <p>
          Time to complete repairs such that the damaged building can support its pre-earthquake
          function.
        </p>
      </div>
    ),
  },
  {
    id: 'groundMotion',
    header: 'Ground Motion Hazard Info',
    body: (
      <div>
        <p>
          Ground motion hazard includes the return periods and corresponding spectral acceleration
          values that define each intensity at which SP3 is run.
        </p>
      </div>
    ),
  },
  {
    id: 'groundMotionInt',
    header: 'Ground Motion Hazard Info',
    body: (
      <div>
        <p>
          Ground motion hazard includes the return periods and corresponding spectral acceleration
          values that define each intensity at which SP3 is run.
        </p>
        <p>
          For international sites, the hazard is approximate and the return periods will not be
          representative of the actual site. It is recommended that the hazard from a site specific
          study is uploaded if the return periods are important for the analysis.
        </p>
        <p>
          <strong>Knowledge Base:</strong>{' '}
          <a
            target="blank"
            href="https://support.sp3risk.com/space/KnowledgeBase/2203779073/Modeling+a+Building+at+an+International+Site"
          >
            Modeling a Building at an International Site
          </a>
        </p>
      </div>
    ),
  },
  {
    id: GROUND_MOTION_METHOD,
    header: 'Ground Motion Method',
    body: (
      <div>
        <b>Options:</b>
        <ul>
          <li>USGS: SP3 automatically retrieves USGS hazard spectra for the selected site.</li>
          <li>
            User Defined: Upload a CSV of hazard spectra by intensity level when using custom
            shaking inputs.
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Determines which ground motion hazard upload source is used in the model.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'groundMotionMethodInt',
    header: 'Ground Motion Method',
    body: (
      <div>
        <b>Options:</b>
        <ul>
          <li>
            Approximate International Hazard: This is an approximate shaking hazard based on the
            specified Level of Seismicity.
          </li>
          <li>
            User Defined: The user may upload return periods and associated acceleration values
            based upon a site specific study.
          </li>
        </ul>
        <b>Default:</b>
        <p>
          The ground motion hazard data are defaulted from the USGS, including peak ground
          acceleration and many spectral acceleration values.
        </p>
      </div>
    ),
  },
  {
    id: HAZARD_EDITION,
    header: 'Hazard Edition',
    body: (
      <div>
        <p>
          Select the edition of the USGS hazard to use in the analysis. The most recent edition
          available is recommended.
        </p>
        <b>Note:</b>
        <p>
          Each edition has a different latitude and longitude range. Depending on your site
          location, you may not be able to use certain editions.
        </p>
      </div>
    ),
  },
  {
    id: 'idr',
    header: 'Peak Story Drift Ratio',
    body: (
      <div>
        <p>
          Peak interstory drift (story displacement/story height) at each story and principal
          direction of the building.
        </p>
        <b>Unit</b>
        <p>rad</p>
        <b>Dimension</b>
        <p>Story</p>
      </div>
    ),
  },
  {
    id: INCLUDE_COLLAPSE,
    header: 'Include Collapse',
    body: (
      <div>
        <p>Select this option to include the consequence of collapse in the analysis.</p>
        <p>
          If included, in each simulation where the building is determined to be collapsed, the
          repair cost is the full replacement cost and the recovery time is the building replacement
          time. This will also affect the number of casualties.
        </p>
      </div>
    ),
  },
  {
    id: INCLUDE_DESIGN_INTENSITIES,
    header: 'Include Design Intensities',
    body: (
      <div>
        <p>
          Select this option to include Design Earthquake (DE) and Maximum Considered Earthquake
          (MCE) intensities in the analysis.
        </p>
        <b>Scenarios:</b>
        <ul>
          <li>
            USGS: The intensities will be auto-calculated and added to the shown return periods.
          </li>
          <li>
            User Defined: User must select the DE/MCE return periods from the list of uploaded
            return periods.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: INCLUDE_PER_REALIZATION_OUTPUT,
    header: 'Include Per Realization Output',
    body: 'Include per realization outputs',
  },
  {
    id: INCLUDE_RESIDUAL_DRIFT,
    header: 'Include Residual Drift Demolition',
    body: (
      <div>
        <p>
          Select this option to incude the consequence of building demolition when residual drifts
          are large.
        </p>
        <p>
          In simulations where the building is determined to have excessive residual drift, the
          damage consequence is building demolition, so the repair cost is the full replacement cost
          and the recovery time is the building replacement time.
        </p>
      </div>
    ),
  },
  {
    id: INCLUDE_SURGE_DEMAND,
    header: 'Include Surge Demand?',
    body: (
      <div>
        <p>
          Include the effects of high demand for material and labor procurement in the event of an
          earthquake that damages a significant number of buildings.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Inclusion will lengthen downtime</li>
        </ul>
      </div>
    ),
  },
  {
    id: INCLUDE_TIME_BASED_ANALYSIS,
    header: 'Run Time Based Analysis',
    body: (
      <div>
        <p>
          Select this option to display output metrics for the expected annual loss. The expected
          annual loss is the average cost per year to repair or replace the building as a result of
          earthquake damage.
        </p>
        <p>
          Note that the annualized loss metrics are only meaningful if there is a wide range of
          shaking intensities analyzed, particularly for more frequent ground shaking (e.g. 50% in
          50 year). See FEMA P-58 Vol. 1, Section 4.4.2 for more information on intensity selection.
        </p>
      </div>
    ),
  },
  {
    id: INCLUDE_USRC,
    header: 'Include USRC Report',
    body: (
      <div>
        <p>
          Select this option to output a United States Resiliency Council (USRC) rating report for
          your building model.
        </p>
        <p>
          To learn more about the USRC, please see their website&nbsp;
          <a href="http://usrc.org" target="_blank" rel="noopener noreferrer">
            here
          </a>
          .
        </p>
      </div>
    ),
  },
  {
    id: 'infilledExteriorWallFraction',
    body: (
      <div>
        <b>Impact on Risk Assessment</b>
        <ul>
          <li>Component population</li>
          <li>Global strength and stiffness defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'intensity',
    header: 'Intensity',
    body: (
      <div>
        <p>
          The probability of exceedance of ground shaking in a given time period; e.g. “10% in 50
          years” corresponds to the level of shaking that has a 10% probability of being exceeded
          over a 50-year period.
        </p>
      </div>
    ),
  },
  {
    id: IS_DENSE_URBAN_AREA,
    header: 'Is Dense Urban Area?',
    body: (
      <div>
        <p>
          If the building is located in a dense urban area, then the surge demand is expected to
          have a larger affect than if the building is not in a dense urban area.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Inclusion will lengthen downtime</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'lbr',
    header: 'Link Beam Rotation',
    body: (
      <div>
        <p>
          Peak link beam rotation angle at each story in each principal direction of the building.
        </p>
        <b>Unit</b>
        <p>rad</p>
        <b>Dimension</b>
        <p>Story</p>
      </div>
    ),
  },
  {
    id: SEISMICITY_DESIGN,
    header: 'Design Code Level of Seismicity',
    body: (
      <div>
        <p>
          For international sites, it is required to specify a level of seismicity associated with
          the Equivalent Building Code. This is used to determine the design code factors used in
          the equivalent design code.
        </p>
        <p>The levels of seismicity (for modern ASCE 7 codes) are as follows:</p>
        <ul>
          <SeismicityLevelLi label="Low" ss="0.2" s1="0.08" />
          <SeismicityLevelLi label="Moderate" ss="0.4" s1="0.16" />
          <SeismicityLevelLi label="Moderately High" ss="0.8" s1="0.32" />
          <SeismicityLevelLi label="High" ss="1.2" s1="0.48" />
          <SeismicityLevelLi label="Very High" ss="2.25" s1="0.9" />
        </ul>
        <p>
          For the older UBC codes, the Level of Seismicity is mapped to the seismic zone of that
          specific code that most closely represents the S<sub>s</sub>
          and S<sub>1</sub>
          values specified above. A general rule of thumb is Low maps to Zone 0 and Very High maps
          to UBC Zone 4.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Design code parameters</li>
          <li>Automated selection of structural components</li>
          <li>Automated selection of non-structural components</li>
          <li>Computed capacities of non-structural components</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <p>
          <strong>Knowledge Base:</strong>{' '}
          <a
            target="blank"
            href="https://support.sp3risk.com/space/KnowledgeBase/2203779073/Modeling+a+Building+at+an+International+Site"
          >
            Modeling a Building at an International Site
          </a>
        </p>
      </div>
    ),
  },
  {
    id: SEISMICITY_SHAKING,
    header: 'Shaking Level of Seismicity',
    body: (
      <div>
        <p>
          This approximates the shaking hazard for the international site (independent of the
          shaking hazard that the building was designed for).
        </p>
        <p>
          The 475 year hazard will be approximately consistent with the following S<sub>s</sub> and
          S<sub>1</sub> values:
        </p>
        <ul>
          <SeismicityLevelLi label="Low" ss="0.2" s1="0.08" />
          <SeismicityLevelLi label="Moderate" ss="0.4" s1="0.16" />
          <SeismicityLevelLi label="Moderately High" ss="0.8" s1="0.32" />
          <SeismicityLevelLi label="High" ss="1.2" s1="0.48" />
          <SeismicityLevelLi label="Very High" ss="2.25" s1="0.9" />
        </ul>
        <p>If a user defined hazard upload is provided, this field will have no effect.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>International shaking hazard approximation</li>
        </ul>
        <p>
          <strong>Knowledge Base:</strong>{' '}
          <a
            target="blank"
            href="https://support.sp3risk.com/space/KnowledgeBase/2203779073/Modeling+a+Building+at+an+International+Site"
          >
            Modeling a Building at an International Site
          </a>
        </p>
      </div>
    ),
  },
  {
    id: 'linesOfResistance',
    header: 'Lines of Resistance',
    body: (
      <div>
        <p>
          The number of interior lines of the lateral force resisting system (LFRS) that have
          significant strength and stiffness.
        </p>
        <p>Lines are assumed to be evenly spaced.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural response prediction</li>
          <li>Global strength and stiffness defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: INCLUDE_IMPEDANCE_LONG_LEAD_TIME,
    header: 'Long Lead Time',
    body: (
      <div>
        <p>
          Lead time (or procurement time) is the amount of time elapsing between the point when an
          order is placed for an item and its delivery to the site. Long lead times for certain
          components (e.g., HVAC equipment and elevators) can increase the time for functional
          recovery.
        </p>
        <p>
          Long lead time should be included in the analysis unless the component procurement process
          is circumvented or pre-arranged with the component suppliers.
        </p>
        <b>Impact on Risk Assessment:</b>
        <p>
          Functional recovery time. Includes the lead-time repair delay of components in the FEMA
          P-58 database that are tagged as &ldquo;long-lead.&ldquo;
        </p>
      </div>
    ),
  },
  {
    id: MASONRY_SHEAR_WALLS_LOAD_BEARING,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ui>
          <li>Red tagging</li>
        </ui>
      </div>
    ),
  },
  {
    id: MAX_WALKABLE_STORY,
    header: 'Max Walkable Story',
    body: (
      <div>
        <p>
          The highest story to which it is acceptable for occupants to access via stairs while the
          elevators are broken while still considering the building functional.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Downtime</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'meanLoss',
    header: 'Mean Loss (SEL)',
    body: (
      <div>
        <p>Scenario Expected Loss - the average repair cost of the building repair/replacement</p>
      </div>
    ),
  },
  {
    id: 'meanLossHeader',
    header: 'SEL',
    body: (
      <div>
        <p>
          Scenario Expected Loss (mean loss) for a ground motion with 10% chance of exceedance in 50
          years.
        </p>
      </div>
    ),
  },
  {
    id: 'medianCapacity',
    header: 'Median Capacity',
    body: (
      <div>
        <p>The demand at which the component has a 50% probability of being damaged.</p>
      </div>
    ),
  },
  {
    id: 'medianLoss',
    header: 'Median Loss (SEL)',
    body: (
      <div>
        <p>There is a 50% probability that the repair cost will not exceed this value.</p>
      </div>
    ),
  },
  {
    id: MINIMUM_NUMBER_OF_EXITS_REQUIRED,
    header: 'Minimum Number of Exits Required for Occupancy',
    body: (
      <p>Minimum number of exits required to meet egress requirements for building occupancy.</p>
    ),
  },
  {
    id: MITIGATION_CONTRACTOR,
    header: 'Contractor Relationship',
    body: (
      <div>
        <p>
          Relationship with the contractor as it relates to their availability to begin repair work
          following an earthquake.
        </p>
        <b>Options:</b>
        <ul>
          <li>None: little or no pre-existing relationship with the contractor.</li>
          <li>
            Good: there is a good relationship with the contractor, such that they will prioritize
            repairing this building.
          </li>
          <li>
            Retainer: there is a pre-arranged contract with the contractor to be available to
            mobilize for repairs.
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Affects contractor mobilization time</li>
        </ul>
      </div>
    ),
  },
  {
    id: MITIGATION_ENGINEER,
    header: 'Engineer on Retainer',
    body: (
      <div>
        <p>
          There is a pre-arranged contract with an engineer to be available for the design of
          repairs.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Reduces engineering mobilization time</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'mitigationFactors',
    header: 'Mitigation Factors',
    body: (
      <div>
        <p>
          Various mitigation factors to reduce the delays to the start of repair after an
          earthquake.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Building Downtime</li>
        </ul>
      </div>
    ),
  },
  {
    id: MITIGATION_FUNDING_SOURCE,
    header: 'Funding Source',
    body: (
      <div>
        <p>Source of funding needed for repairs.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Financing time varies with funding source</li>
        </ul>
      </div>
    ),
  },
  {
    id: MITIGATION_INSPECTOR,
    header: 'Inspector on Retainer',
    body: (
      <div>
        <p>
          There is a pre-arranged contract with an inspector to be available for post-earthquake
          safety inspection. Similar to BORP requirements.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Reduces inspection time</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'modeShapes',
    header: 'Mode Shapes',
    body: (
      <div>
        <p>
          Lateral floor displacements at each level corresponding to the translational modes of
          vibration. The number of modes used by SP3 is equal to the number of stories or three,
          whichever is smaller.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural responses</li>
        </ul>
        <b>Default:</b>
        <p>
          Based on typical mass and stiffness distributions for the building type being analyzed.
        </p>
      </div>
    ),
  },
  {
    id: MODE_SHAPE_METHOD,
    header: 'Mode Shape Source',
    body: (
      <div>
        <b>Options:</b>
        <ul>
          <li>
            SP3 Default: SP3 uses its default mode shape assumptions based on the building inputs
            and response method.
          </li>
          <li>
            User Defined Upload: Upload a CSV of user-defined mode shapes by floor, mode, and
            direction when project-specific results are available.
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Determines which mode shape upload source is used in the model.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'n90thPercentileLoss',
    header: 'Counted 90th Percentile',
    body: (
      <div>
        <p>There is a 90% probability that the repair cost will not exceed this value.</p>
      </div>
    ),
  },
  {
    id: NEED_ELEVATOR_FOR_FUNCTION,
    header: 'Need Elevator for Function?',
    body: (
      <div>
        <p>
          If the tenants of the building (at upper floors) need the elevators in an operational
          state to be considered functional. If elevators are needed, then it is acceptable to have
          a limited amount of non-functional elevators as long as the undamaged elevators can
          reasonably transport the occupants to upper floors.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Downtime</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'newPassword',
    header: 'Password Requirements',
    body: (
      <ul>
        <li>At least 8 characters</li>
        <li>At least 1 lowercase alphabetical character</li>
        <li>
          At least 1 of the following special characters: = + - ^ $ * [ ] {} ( ) ? ! @ # & % | _ ~
        </li>
      </ul>
    ),
  },
  {
    id: NON_STRUCTURAL_RETROFIT_CODE_YEAR,
    body: (
      <div>
        <p>
          This impacts structural responses, component selection, quantity, and damage threshold.
        </p>
        <b>Note:</b>
        <p>
          If &quot;ASCE 2022/ASCE 2016 (non-struct/struct)&quot; is selected, SP3 automations of
          nonstructural component capacities are based on ASCE 7-22 requirements, but SP3 automation
          of structural properties are based on ASCE 7-16 requirements.
        </p>
      </div>
    ),
  },
  {
    id: NON_STRUCTURAL_RETROFIT_YEAR,
    header: 'Non Stuctural Retrofit Year',
    body: 'This impacts the design code to which the non structural components are retrofit.',
  },
  {
    id: NUM_REALIZATIONS,
    header: 'Number of Earthquake Simulations',
    body: 'Number of Monte Carlo simulations to run per intensity level.',
  },
  {
    id: NUMBER_OF_STORIES,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Inherent overstrength/additional stiffness defaulting</li>
          <li>Structural response prediction</li>
          <li>Automated selection of structural components</li>
          <li>Automated selection of non-structural components</li>
          <li>Computed capacities of non-structural components</li>
          <li>Collapse capacity defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: NUMBER_OF_SUBGRADE_STORIES,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Automated selection of structural components</li>
          <li>Automated selection of non-structural components</li>
          <li>Computed capacities of non-structural components</li>
        </ul>
        <b>Unaffected by number of subgrade stories:</b>
        <ul>
          <li>Structural response prediction of superstructure stories</li>
          <li>Building strength/stiffness</li>
          <li>Collapse capacity</li>
        </ul>
      </div>
    ),
  },
  {
    id: OCCUPANCY,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Automated selection of non-structural components</li>
          <li>Building occupant population modeling</li>
          <li>Estimated building replacement value</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'occupancyByStoryOccupancy',
    header: 'Occupancy',
    body: (
      <div>
        <p>The primary use of the stories.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Automated selection of non-structural components</li>
          <li>Building occupant population modeling</li>
          <li>Estimated building replacement value</li>
        </ul>
      </div>
    ),
  },
  {
    id: OCCUPANCY_COST_MULTIPLIER,
    header: 'Occupancy Cost Multiplier',
    body: (
      <div>
        <p>
          Cost multiplier to increase or decrease component repair costs based on the occupancy of
          the building.
        </p>
        <b>Impact of Risk Assessment:</b>
        <ul>
          <li>Scales component repair costs.</li>
        </ul>
        <b>Example:</b>
        <p className="pl-3">
          Hospitals are more expensive to repair than typical buildings, therefore the Occupancy
          Cost Multiplier should be greater than 1.0 for hospitals.
        </p>
        <b>Note:</b>
        <p>
          This factor does not scale building replacement cost. Occupancy is already considered
          separately in SP3 building replacement cost defaulting.
        </p>
      </div>
    ),
  },
  {
    id: 'oop_drift',
    header: 'Out of Plane Drift',
    body: (
      <div>
        <p>
          Peak out of plane drift (ratio of relative deflection at the mid height of the wall to the
          wall span height, see figure) demands on out-of-plane walls at each story and principal
          direction of the building.
        </p>
        <b>Unit</b>
        <p>rad</p>
        <b>Dimension</b>
        <p>Story</p>
      </div>
    ),
  },
  {
    id: 'oop_force',
    header: 'Out of Plane Connection Force',
    body: (
      <div>
        <p>
          Peak out of plane force in the connection between walls and diaphragm at each floor and
          principal direction of the building.
        </p>
        <b>Unit</b>
        <p>g</p>
        <b>Dimension</b>
        <p>Story</p>
      </div>
    ),
  },
  {
    id: 'p58Parallel',
    header: 'FEMA P58 Parallel',
    body: (
      <div>
        <p>
          Time to repair the damaged building to its pre-earthquake state assuming that all floors
          are repaired simultaneously.
        </p>
      </div>
    ),
  },
  {
    id: 'p58Serial',
    header: 'FEMA P58 Series',
    body: (
      <div>
        <p>
          Time to repair the damaged building to its pre-earthquake state assuming that floors are
          repaired sequentially.
        </p>
      </div>
    ),
  },
  {
    id: PERCENT_GLAZING,
    body: (
      <div>
        <p>
          This impacts which components are selected and affects strength and stiffness of the
          building.
        </p>
      </div>
    ),
  },
  {
    id: PERCENT_OF_EXITS_REQUIRED,
    header: 'Percentage of Exits Required for Occupancy',
    body: (
      <p>
        Minimum percentage of exits required to meet egress requirements for building occupancy.
      </p>
    ),
  },
  {
    id: PERCENT_OF_PARTITIONS_ARE_FIRE_BRAKE,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ui>
          <li>Downtown</li>
        </ui>
      </div>
    ),
  },
  {
    id: 'performanceGroup',
    header: 'Performance Group',
    body: (
      <div>
        <p>
          A subset of components described by a single fragility that will be exposed to the same
          demand in response to earthquake shaking.
        </p>
        <p>More than one performance group may be desirable if you have components that are:</p>
        <ul>
          <li>
            on the same level, but will experience different demands from earthquake shaking (e.g.
            different drift demands on different frame lines)
          </li>
          <li>
            correlated, but only in certain areas of the building (e.g. tilt-up wall-to-diaphragm
            connections that are located on opposite sides of the building)
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: 'period1',
    header: (
      <span>
        1<sup>st</sup> Mode Period (T
        <sub>1</sub>)
      </span>
    ),
    body: (
      <div>
        <p>
          First mode (translational) period in each direction. This period is for the “as-built”
          condition which includes gravity system and non-structural component contributions (not
          just the lateral force resisting system).
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural response prediction</li>
          <li>
            Anchoring of spectral acceleration for intensity assessments and collapse fragility
          </li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Based on structural system, building height, design code, and level of seismicity.
        </p>
      </div>
    ),
  },
  {
    id: 'period2',
    header: (
      <span>
        2<sup>nd</sup> Mode Period (T
        <sub>1</sub>)
      </span>
    ),
    body: (
      <div>
        <p>
          Second mode (translational) period in each direction. This period is for the “as-built”
          condition which includes gravity system and non-structural component contributions (not
          just the lateral force resisting system).
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural response prediction</li>
          <li>
            Anchoring of spectral acceleration for intensity assessments and collapse fragility
          </li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Based on structural system, building height, design code, and level of seismicity.
        </p>
      </div>
    ),
  },
  {
    id: 'period3',
    header: (
      <span>
        3<sup>rd</sup> Mode Period (T
        <sub>3</sub>)
      </span>
    ),
    body: (
      <div>
        <p>
          Third mode (translational) period in each direction. This period is for the “as-built”
          condition which includes gravity system and non-structural component contributions (not
          just the lateral force resisting system).
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural response prediction</li>
          <li>
            Anchoring of spectral acceleration for intensity assessments and collapse fragility
          </li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Based on structural system, building height, design code, and level of seismicity.
        </p>
      </div>
    ),
  },
  {
    id: 'pfa',
    header: 'Peak Floor Acceleration',
    body: (
      <div>
        <p>Peak floor acceleration at each floor and principal direction of the building.</p>
        <b>Unit</b>
        <p>g</p>
        <b>Dimension</b>
        <p>Floor</p>
      </div>
    ),
  },
  {
    id: 'pfv',
    header: 'Peak Floor Velocity',
    body: (
      <div>
        <p>Peak floor velocity at each floor and principal direction of the building.</p>
        <b>Unit</b>
        <p>m/s</p>
        <b>Dimension</b>
        <p>Floor</p>
      </div>
    ),
  },
  {
    id: 'pga',
    header: 'Peak Ground Acceleration',
    body: (
      <div>
        <p>
          Peak ground acceleration (PGA) is equal to the maximum ground acceleration that occurred
          during earthquake shaking at a location
        </p>
      </div>
    ),
  },
  {
    id: PLAN_TORSION,
    body: (
      <div>
        <b>Options Include:</b>
        <ul>
          <li>None: No irregularity</li>
          <li>Moderate: Torsional Irregularity (1a)</li>
          <li>Extreme: Extreme Torsional Irregularity (1b)</li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Global strength and stiffness defaulting</li>
          <li>Structural response prediction</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No torsion irregularity.</p>
      </div>
    ),
  },
  {
    id: PREQUALIFIED_ACC_GROUND,
    header: 'Prequalified Equipment Acceleration Level at Ground Level',
    body: (
      <div>
        <p>
          The level of acceleration at the ground level for which prequalified equipment is
          qualified. SP3 uses this acceleration to estimate the capacity of prequalified equipment
          components at the ground level.
        </p>
        <b>Note:</b>
        <p>
          Accelerations for levels between the ground and roof are interpolated. For a given
          component, SP3 assumes a prequalification acceleration based on the highest level on which
          the component is located.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ui>
          <li>Automation of prequalified equipment component capacities.</li>
        </ui>
      </div>
    ),
  },
  {
    id: PREQUALIFIED_ACC_ROOF,
    header: 'Prequalified Equipment Acceleration Level at Roof Level',
    body: (
      <div>
        <p>
          The level of acceleration at the roof level for which prequalified equipment is qualified.
          SP3 uses this acceleration to estimate the capacity of prequalified equipment components
          at the roof level.
        </p>
        <b>Note:</b>
        <p>
          Accelerations for levels between the ground and roof are interpolated. For a given
          component, SP3 assumes a prequalification acceleration based on the highest level on which
          the component is located.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ui>
          <li>Automation of prequalified equipment component capacities.</li>
        </ui>
      </div>
    ),
  },
  {
    id: PREQUALIFIED_DESIGN_CODE_YEAR,
    header: 'Pre-qualified Equipment Design Code Year',
    body: (
      <div>
        <p>
          If this option is used, the level of acceleration that is assumed for component
          prequalification is determined based on minimum building code requirements for the
          building code that is selected.
        </p>
        <b>Note:</b>
        <p>
          If you would like to set these accelerations levels yourself - choose &quot;User
          Defined&quot;
        </p>
        <b>Impact on Risk Assessment:</b>
        <ui>
          <li>Automation of pre-qualified equipment component capacities.</li>
        </ui>
      </div>
    ),
  },
  {
    id: PROB_COLLAPSE_MCE,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Collapse capacity</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Based on the default FEMA P-154 score obtained using a simplified FEMA P-154 checklist
          based on specified structural system type, building age, soil type, irregularities, and
          risk category.
        </p>
        <p>
          <strong>Knowledge Base:</strong>{' '}
          <a
            target="blank"
            href="https://support.sp3risk.com/space/KnowledgeBase/1777633972/Collapse+Automation#Note-on-S-a-(MCE-R-)-Anchoring"
          >
            Note on Sa(MCER) Anchoring
          </a>
        </p>
      </div>
    ),
  },
  {
    id: 'quantityDir1',
    header: 'Direction 1 Quantity',
    body: (
      <div>
        <p>
          This is the component quantity in direction 1 for each location specified in the first
          column.
        </p>
      </div>
    ),
  },
  {
    id: 'quantityDir2',
    header: 'Direction 2 Quantity',
    body: (
      <div>
        <p>
          This is the component quantity in direction 2 for each location specified in the first
          column.
        </p>
      </div>
    ),
  },
  {
    id: 'quantityNonDir',
    header: 'Non Directional Quantity',
    body: (
      <div>
        <p>This is the component quantity for each location specified in the first column.</p>
      </div>
    ),
  },
  {
    id: 'quantityPerUnit',
    header: 'Quantity Per Unit',
    body: (
      <div>
        <p>Relates the fragility “unit” to a physical quantity.</p>
        <b>Example:</b>
        <p className="pl-3">
          “100 sf” means that for every 100 square feet of the component you would populate 1 unit
          of the component.
        </p>
      </div>
    ),
  },
  {
    id: 'rediOutput',
    header: 'REDi Repair Time (2013) (without impeding factors)',
    body: (
      <p>
        The recovery times shown here do <strong>not</strong> include the impeding factors that are
        selected by the user. The REDi method impeding factor calculation is only calibrated to the
        475/Design level event. The REDi downtime estimation for the 475/Design level event is
        available in the PDF reports.
      </p>
    ),
  },
  {
    id: REGION_COST_MULTIPLIER,
    header: 'Region Cost Multiplier',
    body: (
      <div>
        <p>Cost multiplier to account for regional variation in construction and repair costs.</p>
        <b>Impact of Risk Assessment</b>
        <ul>
          <li>Default building cost per square foot and component repair costs.</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Value based on location (state).</p>
      </div>
    ),
  },
  {
    id: 'residualDriftModel',
    header: 'Residual Drift Model',
    body: (
      <div>
        <p>
          Model for converting peak transient story drift ratio to median residual story drift
          ratio. Legacy models are available for comparison, but not recommended. Documentation of
          current models will soon be available in the FEMA P58 background document, FEMA
          P-58/BD-3.7.26.
        </p>
        <b>Note:</b>
        <p className="pl-3">
          Most BRBF buildings should use the &ldquo;BRBF&rdquo; model; the &ldquo;BRBF Pinned
          Framing&rdquo; model assumes that beam and column splice connections in the BRBF frame
          behave as pinned.
        </p>
        <b>Default:</b>
        <p className="pl-3">Based on structural system type.</p>
      </div>
    ),
  },
  {
    id: 'residualDriftDistributionModel',
    header: 'Distribution Model',
    body: (
      <div>
        <p>
          Probability distribution model for simulating residual story drift ratio conditioned on
          peak transient story drift ratio.
        </p>
        <b>Default:</b>
        <p className="pl-3">Weibull, according FEMA P-58/BD-3.7.26</p>
      </div>
    ),
  },
  {
    id: 'residualDriftDispersion',
    header: 'Dispersion/COV',
    body: (
      <div>
        <p>
          This is the variability of residual story drift ratio conditioned on the realized peak
          story drfit ratio. If the distribution is lognormal, it is the logarithmic disperison. If
          the distribution is Weibull, it is the coeficient of variation.
        </p>
        <b>Default:</b>
        <p className="pl-3">0.5, according FEMA P-58/BD-3.7.26</p>
      </div>
    ),
  },
  {
    id: 'residualDriftRecordToRecordDispersion',
    header: 'Record-to-Record Dispersion of Residual Drift',
    body: (
      <div>
        <p>
          Record-to-record dispersion of Residual Story Drift Ratio, conditioned on the median peak
          story drift ratio. The total dispersion of residual residual story drift ratio is
          determined by combining this value with the modeling uncertainty by the
          square-root-sum-of-squars (SRSS) method.
        </p>
        <b>Example:</b>
        <p>
          If the record-to-record dispersion is 0.8 and the modeling uncertainty is 0.35, the total
          dispersion of residual drift is srss(0.8, 0.35) = 0.87.
        </p>
        <b>Default:</b>
        <p>0.8</p>
      </div>
    ),
  },
  {
    id: RESIDUAL_DRIFT_SIMULATION_METHOD,
    header: 'Residual Drift Simulation Method',
    body: (
      <div>
        <p>
          Residual drift story ratios may be simulated from median story drift ratio or from
          realized story drift ratios.
        </p>
        <p>
          The original FEMA P58 method was to simulate residual story drift ratio based on the
          median story drift ratio, using a lognormal record-to-record dispersion 0.8; for typical
          cases where modeling dispersion is 0.35, that results in a total dispersion of 0.87 for
          residual drifts.
        </p>
        <p>
          An alternative beta method is to simulate residual story drift ratio from the realized
          story drift ratio in each individual Monte Carlo simulation. Since record-to-record
          uncertainty and modeling uncertainty are already incorporated in the simulation of story
          drift ratios, only the uncertainty of residual story drift ratio given peak story drift
          ratio is required for this simulation method; a Weibull distribution with COV of 0.5 is
          recommended. Simulating residual drifts from realized drifts is a more advanced method;
          however, the current state of the art for calibrating this method is not yet widely
          accepted. Due to the nonlinear relationship between peak story drift ratio and residual
          story drift ratio, residual drift simulations by this method tend to have extremely large
          variability (COV of ~1.4 at DE and MCE level shaking). As a result, the beta version of
          this method results in very large estimations of residual drift demolition probability
          even at intensities for which expected residual drift is very small.
        </p>
        <b>Default:</b>
        <p>Simulate residual drifts from median story drift ratio (original FEMA P58)</p>
      </div>
    ),
  },
  {
    id: 'reoccupancy',
    header: 'Re-Occupancy',
    body: (
      <div>
        <p>Time to complete repairs such that the damaged building is safe to use as shelter.</p>
      </div>
    ),
  },
  {
    id: 'repairCostBreakdown',
    header: 'Repair Cost Breakdown',
    body: (
      <div>
        <p>
          The expected cost to repair the damaged building to its pre-earthquake state disaggregated
          by category (i.e. losses due to collapse, residual drift, and each category of building
          components). This is presented as a percent of the total building replacement value.
        </p>
      </div>
    ),
  },
  {
    id: 'repairCostByIntensity',
    header: 'Repair Cost By Intensity',
    body: (
      <div>
        <p>
          The cost to repair the damaged building to its pre-earthquake state presented as a percent
          of the total building replacement value.
        </p>
      </div>
    ),
  },
  {
    id: 'repairCostPerUnit',
    header: 'Repair Cost Per Unit',
    body: (
      <div>
        <p>
          The amount it will cost to repair a damaged unit of the component, including the effects
          of volume discounting (as more units are repaired, they may become cheaper to repair).
        </p>
        <p>
          The displayed cost is in 2011 US dollars (because that is when the FEMA P-58 database was
          created) and a date-based inflation factor is used to increase cost to current day.
        </p>
        <p>
          For damaged quantities that fall between the “Low” and “High” quantity thresholds, the
          per-unit repair cost is linearly interpolated.
        </p>
      </div>
    ),
  },
  {
    id: 'repairCostExposureHeader',
    header: 'Exposure (Potential for Repair Costs)',
    body: (
      <div>
        <p>
          For each component group, this is the cost to completely replace the components throughout
          the building, assuming the most expensive damage state for all components (includes volume
          discounting).
        </p>
        <p>
          Note: this does not need to add up to the total building replacement value, but rather
          gives a sense of how much potential each component group has to contribute to the loss.
        </p>
      </div>
    ),
  },
  {
    id: REPAIR_COSTS_SCALE_WITH_BUILDING_VALUE,
    header: 'Scale component repair costs with building value',
    body: (
      <div>
        <p>
          This option scales component costs so that their repair costs relative to the value of the
          building is consistent with expected values, based on SP3 defaults.
        </p>
        <b>Example:</b>
        <p>
          If the user-defined Total Cost per Square Foot is double the SP3 default Total Cost per
          Square Foot, then a factor of 2.0 is applied to component repair costs.
        </p>
        <b>Note:</b>
        <p>
          Additional cost modifiers for component groups, which compound with this effect, can be
          specified in the building components page.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Building value modifier applied to component repair costs.</li>
        </ul>
      </div>
    ),
  },
  {
    id: REPAIR_TIME_METHOD,
    header: 'Recovery time method',
    body: (
      <div>
        <p>Select the method that is used to calculate the building recovery time.</p>
        <ul>
          <li>
            REDi (2013): The REDi methodology was published by Arup in 2013. This method estimates
            building re-occupancy, functional recovery, and full recovery times based on
            component-level damage; impedance factors (inspection, contractor, etc.) are only
            included at one intensity level (the 475 or DE).
          </li>
          <li>
            ATC-138 Beta: The ATC-138 functional recovery method estimates building re-occupancy,
            functional recovery, and full recovery times through a series of fault trees to
            explicitly map component damage to system operation and building function. Downtimes,
            including impedance factors, are assessed at every shaking intensity. This method is
            currently under development.
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Recovery Time (down time) Calculation</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'repairTimePerUnit',
    header: 'Repair Time Per Unit',
    body: (
      <div>
        <p>
          The worker days it will take to repair a damaged unit of the component, including economy
          of scale (as more units are repaired, they may become faster to repair per unit).
        </p>
        <p>
          For damaged quantities that fall between the &ldquo;Low&rdquo; and &ldquo;High&rdquo;
          quantity thresholds, the per-unit repair time is linearly interpolated.
        </p>
      </div>
    ),
  },
  {
    id: RETURN_PERIOD_DE,
    header: 'DE',
    body: (
      <div>
        <p>
          The Design Earthquake (DE) shaking determined based on the most current design code and
          hazard curves.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>
            If the REDi recovery time option is selected, the recovery time will be calculated at
            the more intense of the DE and 475 year intensity.
          </li>
          <li>The USRC rating is determined at the more intense of the DE and 475 year shaking.</li>
        </ul>
      </div>
    ),
  },
  {
    id: RETURN_PERIOD_MCE,
    header: 'MCE',
    body: (
      <div>
        <p>The current code Maximum Considered Earthquake return period.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>
            The probability of collapse at MCE calculated form FEMA P-154 is anchored using this
            shaking hazard (otherwise the code MCE spectra is used).
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: RETURN_PERIODS,
    header: 'Return Period',
    body: (
      <div>
        <p>
          The average time (in years) between ground motions equal to or greater than the specified
          intensity.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>
            Intensity-based risk analysis results (e.g. repair cost, recovery time, etc.) will be
            output for each return period specified.
          </li>
          <li>Time-based assessments are also based upon the return periods defined here.</li>
        </ul>
        <b>Default:</b>
        <p>
          Nine return periods are defaulted between 22 and 2475 years. This range of intensities
          supports intensity-based assessment as well as time-based assessment.
        </p>
      </div>
    ),
  },
  {
    id: 'returnPeriodsForGroundMotionUpload',
    header: 'Return Period',
    body: (
      <div>
        <p>
          The average time (in years) between ground motions equal to or greater than the specified
          intensity.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>
            Intensity-based risk analysis results (e.g. repair cost, recovery time, etc.) will be
            output for each return period specified.
          </li>
          <li>Time-based assessments are also based upon the return periods defined here.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'ridr',
    header: 'Residual Story Drift Ratio',
    body: (
      <div>
        <p>
          Residual story drift ratio (perminent story displacement/story height) at each story and
          principal direction of the building.
        </p>
        <b>Unit</b>
        <p>rad</p>
        <b>Dimension</b>
        <p>Story</p>
      </div>
    ),
  },
  {
    id: RISK_CATEGORY,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Design code parameters</li>
          <li>Automated selection of non-structural components (e.g. ceilings)</li>
          <li>Computed capacities of non-structural components</li>
          <li>Collapse capacity defaulting (capacity increase for higher risk categories)</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Based on building occupancy</p>
      </div>
    ),
  },
  {
    id: 'rollupLocations',
    header: 'Locations',
    body: (
      <div>
        <p>
          Location of all components across the height of the building. The displayed locations
          include all performance groups associated with the component.
        </p>
        <b>Example 3-story building locations:</b>
        <div className="pt-3">
          <div className={locationsImage} />
        </div>
      </div>
    ),
  },
  {
    id: 'saOverVUlt',
    header: (
      <span>
        S<sub>a</sub>
        (T
        <sub>1</sub>
        )/ v<sub>ult</sub>
      </span>
    ),
    body: (
      <div>
        <p>
          The ratio of spectral acceleration at the fundamental period of the building to ultimate
          base shear strength.
        </p>
      </div>
    ),
  },
  {
    id: 'rollupTotalQuantity',
    header: 'Quantity',
    body: (
      <div>
        <p>Total quantity of components across all levels, directions, and performance groups.</p>
      </div>
    ),
  },
  {
    id: SEISMIC_IMPORTANCE_FACTOR,
    header: (
      <span>
        Seismic Importance Factor, I<small>e</small>
      </span>
    ),
    body: (
      <div>
        <p>
          Code factor for strength design (see ASCE 7-16 Section 1.5). Changing this factor will not
          affect the risk category. This will only affect building codes UBC 1976 and later.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Global strength and stiffness defaulting</li>
          <li>Collapse capacity increase for higher Ie</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Based on assigned Risk Category</p>
      </div>
    ),
  },
  {
    id: PERFORMANCE_GROUP_SERVICE_LOCATIONS,
    header: 'Service Locations',
    body: (
      <div>
        <p>
          The service locations for a component are the
          <strong>stories</strong> serviced by that component. Service locations can be expressed as
          a comma separated list of stories (1, 2, 3) or ranges (1-3, 5-7) of stories
        </p>
      </div>
    ),
  },
  {
    id: MODEL_NAME,
    body: (
      <div>
        <p>
          Downloaded files are prefixed with it. If your model name were <b>Downtown Tower A</b>,
          downloads would be named like:
        </p>
        <ul>
          <li>Downtown Tower A - Risk Analysis Report.pdf</li>
          <li>Downtown Tower A - Site Hazards Report.pdf</li>
        </ul>
      </div>
    ),
  },
  {
    id: SITE_CLASS,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Design code parameters</li>
          <li>USGS seismic shaking hazard lookup</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Based on the&nbsp;
          <a href="https://earthquake.usgs.gov/data/vs30" target="_blank" rel="noopener noreferrer">
            USGS Global Vs30 Model
          </a>
        </p>
      </div>
    ),
  },
  {
    id: 'siteCoordinates',
    header: 'Site Coordinates',
    body: (
      <div>
        <p>
          Modifying the coordinates here will not change the address that is associated with the
          model.
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Soil properties defaulting</li>
          <li>USGS seismic shaking hazard lookup</li>
          <li>Design code parameters</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'siteCoordinatesLocked',
    header: 'Why is my site location locked?',
    body: (
      <div>
        <p>The address, latitude and longitude can not be changed after the model has been run.</p>
        <p>
          To assess this same model at a different site, use Copy Model on the Models dashboard.
        </p>
      </div>
    ),
  },
  {
    id: 'story',
    header: 'Stories',
    body: (
      <div>
        <p>
          Stories can be expressed as a comma separated list of values (1, 2, 3) or ranges (1-3,
          5-7).
        </p>
        <b>Note:</b>
        <ul>
          <li>
            Subgrade stories are denoted with a &quot;B&quot; prefix and count from the base level
            down.
          </li>
        </ul>
        <b>Example ranges include:</b>
        <ul>
          <li>1, 2, 3 (superstructure levels 1, 2, and 3)</li>
          <li>1-3 (superstructure levels 1 through 3)</li>
          <li>B2,B1 (basement levels 2 and 1)</li>
          <li>B3-B1 (basement levels 3 through 1)</li>
          <li>B3-5 (basement level 3 through superstructure level 5)</li>
        </ul>
        <b>Example 3-Story Building</b>
        <div className="pt-3">
          <div className={storiesImage} />
        </div>
      </div>
    ),
  },
  {
    id: 'structuralResponseSource',
    header: 'Structural Response Source',
    body: (
      <div>
        <b>Options:</b>
        <ul>
          <li>
            <b>SP3 Response Prediction Engine:</b> SP3 automatically estimates structural responses
            across intensities using its built-in model.
          </li>
          <li>
            <b>ASCE 7-22 Design Demands:</b> Uses ASCE 7-22 code-based demand relationships to
            estimate responses.
          </li>
          <li>
            <b>User Defined Response History Analysis:</b> Use when you have nonlinear RHA results
            for all intensities and want SP3 to use them directly.
          </li>
          <li>
            <b>User Defined Medians:</b> Use median-only response inputs for quick sensitivity
            checks without full RHA data.
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Determines which structural response upload source is used in the model.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'sampleUploadGuideRha',
    header: 'Upload Guide',
    body: (
      <a target="blank" href="https://support.sp3risk.com/space/KnowledgeBase/1777633523">
        See Response History Upload Guide
      </a>
    ),
  },
  {
    id: 'sampleUploadGuideMedians',
    header: 'Upload Guide',
    body: (
      <a target="blank" href="https://support.sp3risk.com/space/KnowledgeBase/1777633540">
        See Response Median Upload Guide
      </a>
    ),
  },
  {
    id: 'sampleUploadGuideGroundMotion',
    header: 'Upload Guide',
    body: (
      <a target="blank" href="https://support.sp3risk.com/space/KnowledgeBase/2646704129">
        See Ground Motion Upload Guide
      </a>
    ),
  },
  {
    id: 'sampleUploadGuideModeShape',
    header: 'Upload Guide',
    body: (
      <a target="blank" href="https://support.sp3risk.com/space/KnowledgeBase/2646474753">
        See Mode Shape Upload Guide
      </a>
    ),
  },
  {
    id: STRUCTURAL_RETROFIT_CODE_YEAR,
    body: (
      <div>
        <p>
          This impacts structural responses, component selection, quantity, and damage threshold.
        </p>
        <b>Note:</b>
        <p>
          If &quot;ASCE 2022/ASCE 2016 (non-struct/struct)&quot; is selected, SP3 automations of
          nonstructural component capacities are based on ASCE 7-22 requirements, but SP3 automation
          of structural properties are based on ASCE 7-16 requirements.
        </p>
      </div>
    ),
  },
  {
    id: STRUCTURAL_RETROFIT_YEAR,
    header: 'Structural Retrofit Year',
    body: 'This impacts structural responses, component selection, quantity, and damage threshold.',
  },
  {
    id: 'structuralSystem',
    header: 'Structural System',
    body: (
      <div>
        <b>Acronyms:</b>
        <ul>
          <li>BRBF: Buckling Restrained Brace Frame</li>
          <li>CBF: Concentric Braced Frame</li>
          <li>PRESS: Precast Seismic Structural System</li>
          <li>RC: Reinforced Concrete</li>
          <li>RM: Reinforced Masonry</li>
          <li>WLF: Wood Light Frame</li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Design code parameters</li>
          <li>Expected overstrength/additional stiffness defaulting</li>
          <li>Structural response prediction</li>
          <li>Automated selection of structural components</li>
          <li>Collapse capacity defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: SR_RECORD_TO_RECORD_DISPERSION_MODEL,
    header: 'Record-to-record Dispersion Model',
    body: (
      <div>
        <p>
          If FEMA P58 is selected, record-to-record dispersion values from FEMA P-58-1 Table 5-6 are
          used for simulating structural responses.
        </p>
        <p>
          If &ldquo;Dispersion = 0.4&rdquo; is selected, record-to-record dispersion of 0.4 is used
          for all structual responses at all intensities.
        </p>
        <b>Default:</b>
        <p>FEMA P58 record-to-record dispersion model.</p>
      </div>
    ),
  },
  {
    id: SR_MODELING_DISPERSION,
    header: 'Model Dispersion',
    body: (
      <div>
        <p>
          Modeling dispersion is additional dispersion that is added to the simuluation to account
          for uncertainty in construction quality and quality of the analytical model. See FEMA
          P-58-1 section 5.2.6 for determination of modeling dispersion. Where the quality of
          construction is superior (e.g. hospitals that require significant QA/QC), the modeling
          dispersion may be reduced to 0.27.
        </p>
        <b>Default:</b>
        <p>
          Default value is 0.35, which is based on average model quality and average construction
          quality.
        </p>
      </div>
    ),
  },
  {
    id: 'tdmfBraceConfig',
    body: (
      <div>
        <b>Impact on Risk Assessment</b>
        <ul>
          <li>Component population</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Chevron</p>
      </div>
    ),
  },
  {
    id: 'tdmfBayLength',
    body: (
      <div>
        <b>Impact on Risk Assessment</b>
        <ul>
          <li>Component population</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">25 feet</p>
      </div>
    ),
  },
  {
    id: 'tdmfDamperExposure',
    body: (
      <div>
        <b>Impact on Risk Assessment</b>
        <ul>
          <li>
            Component population. Repair costs and repair times for exposed damper assemblies are
            lower than when they are concealed within a wall.
          </li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">In wall</p>
      </div>
    ),
  },
  {
    id: 'timeScaleFactor',
    header: 'Time Scale Factor',
    body: (
      <div>
        <p>
          Scale factor to apply to the component repair time consequences; e.g. a scale factor of
          1.5 would increase the component repair time by 50%.
        </p>
        <b>Default:</b>
        <p className="pl-3">Default scale factor is 1.</p>
      </div>
    ),
  },
  {
    id: TRUNCATE_COLLAPSE_DISTRIBUTION,
    header: 'Collapse Distribution Truncation',
    body: (
      <div>
        <p>
          Truncates collapse fragility distribution by converting the probability of collapse to
          zero when the demand is below a specified number of standard deviations from the mean.
        </p>
      </div>
    ),
  },
  {
    id: TRUNCATE_COLLAPSE_DISTRIBUTION_LEVEL,
    header: 'Collapse Distribution Truncation Level',
    body: (
      <div>
        <p>
          Truncates collapse fragility distribution by converting the probability of collapse to
          zero when the demand is below a specified number of standard deviations from the mean.
        </p>
        <b>Default:</b>
        <p className="pl-3">
          Default truncation level is -2.32 standard deviations, which means that collapse will not
          be simulated when the probability is less than 1%.
        </p>
      </div>
    ),
  },
  {
    id: TRUNCATE_COMPONENT_DISTRIBUTION,
    header: 'Component Distribution Truncation',
    body: (
      <div>
        <p>
          Truncates component fragility distributions by converting the probability of damage to
          zero when the demand is below a specified number of standard deviations from the mean.
        </p>
      </div>
    ),
  },
  {
    id: TRUNCATE_COMPONENT_DISTRIBUTION_LEVEL,
    header: 'Component Distribution Truncation Level',
    body: (
      <div>
        <p>
          Truncates component fragility distributions by converting the probability of damage to
          zero when the demand is below a specified number of standard deviations from the mean.
        </p>
        <b>Default:</b>
        <p className="pl-3">
          Default truncation level is -2.0 standard deviations, which means that component damage
          will not be simulated when the probability is less than 2.3%. For high QA/QC (e.g.
          hospital buildings), a value of -1.5 is recommended.
        </p>
      </div>
    ),
  },
  {
    id: 'typeOfResistance',
    header: 'Type of Resistance',
    body: (
      <div>
        <p>
          The source of significant strength and stiffness for the internal lines of the lateral
          force resisting system (LFRS).
        </p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural response prediction</li>
          <li>Global strength and stiffness defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: USE_SMART_EQUIPMENT_COMPONENTS,
    header: 'Use Smart Equipment Components',
    body: (
      <div>
        <p>
          &quot;Smart&quot; components use multiple fragility functions to model an individual
          building component, such that various failure modes (e.g., failure of anchorage only,
          damage to equipment only, or damage to both anchorage and equipment) are captured more
          precisely. Components are modeled with separate fragilities that represent anchorage,
          unanchored equipment (or piping), and anchored equipment (or piping). For cases where the
          anchorage fails, the equipment (or piping) is modeled using the unanchored fragility
          function. For cases in which the anchorage does not fail, the fully anchored equipment (or
          piping) fragility is used.
        </p>
      </div>
    ),
  },
  {
    id: USE_SMART_PIPING_COMPONENTS,
    header: 'Use Smart Piping Components',
    body: (
      <div>
        <p>
          &quot;Smart&quot; piping components use multiple fragility functions to model an
          individual building component, such that various failure modes (e.g., failure of bracing
          only, damage to piping only, or damage to both bracing and piping) are captured more
          precisely. Components are modeled with separate fragilities that represent bracing,
          unbraced piping, and braced piping. For cases where the bracing fails, the piping is
          modeled using the unbraced fragility function. For cases in which the bracing does not
          fail, the braced piping fragility is used.
        </p>
      </div>
    ),
  },
  {
    id: STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS,
    header: 'Use as Default for Components',
    body: (
      <div>
        <p>Sets the default response for components within the same edp.</p>
        <b>Note:</b>
        <p>This is most valuable when you have multiple responses with the same EDP.</p>
        <b>Example:</b>
        <p>
          You have 2 Peak Story Drift Ratio responses in your model:
          <ul>
            <li>Peak Story Drift Ratio 1</li>
            <li>Peak Story Drift Ratio 2</li>
          </ul>
          You want the majority of components to use &quot;Peak Story Drift Ratio 1&quot; as your
          response. So you would check &quot;Yes&quot; for Peak Story Drift Ratio 1 and
          &quot;No&quot; for Peak Story Drift Ratio 2.
        </p>
        <p>
          For any component that you want Peak Story Drift Ratio 2 to be used, you would go to that
          component and manually change &quot;Use Default&quot; to &quot;Peak Story Drift Ratio
          2&quot;.
        </p>
      </div>
    ),
  },
  {
    id: USE_UPDATED_ELEVATOR_COMPONENTS,
    header: 'Use Updated Elevator Components',
    body: (
      <div>
        <p>
          Component modeling for elevators is undergoing substantial improvement as part of the
          ATC-138 project. These improvements include separating the elevator system into multiple
          components and refining the capacities of those components to be more consistent with ASME
          elevator design and construction practices. Updated elevator components are intended for
          buildings that have elevator design and installation conforming to ASME 17.1 2007 or
          similar.
        </p>
      </div>
    ),
  },
  {
    id: VERTICAL_IRREGULARITY,
    body: (
      <div>
        <b>Options Include:</b>
        <ul>
          <li>None: No irregularity</li>
          <li>
            Moderate: Indicates “Stiffness-Soft Story Irregularity” (1a) and/or “Discontinuity in
            Lateral Strength-Weak Story Irregularity” (5a)
          </li>
          <li>
            Extreme: Indicates “Stiffness-Extreme Soft Story Irregularity” (1b) and/or
            “Discontinuity in Lateral Strength-Extreme Weak Story Irregularity” (5b)
          </li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Global strength and stiffness defaulting</li>
          <li>Structural response prediction</li>
          <li>Automated selection of structural components</li>
          <li>Collapse capacity defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">No soft or weak story.</p>
      </div>
    ),
  },
  {
    id: 'vs30',
    header: 'Vs30',
    body: 'Average shear wave velocity in the first 30 meters below the ground surface.',
  },
  {
    id: YEAR_OF_CONSTRUCTION,
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Design code parameters</li>
          <li>Automated selection of structural components</li>
          <li>Automated selection of non-structural components</li>
          <li>Computed capacities of non-structural components</li>
          <li>Collapse capacity defaulting</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'yieldStoryDrift',
    header: 'Yield Story Drift',
    body: (
      <div>
        <p>Story drift at which yielding first occurs.</p>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Structural response prediction (strong influence on residual drift prediction)</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Based on structural system.</p>
      </div>
    ),
  },
  {
    id: 'ylbfBayLength',
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Component population</li>
          <li>Global strength and stiffness defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">25 feet.</p>
      </div>
    ),
  },
  {
    id: 'ylbfBraceConfig',
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Component population</li>
          <li>Global strength and stiffness defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">Chevron</p>
      </div>
    ),
  },
  {
    id: 'ylbfBraceExposure',
    body: (
      <div>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>
            Component population. Component population. Repair costs and repair times for exposed
            brace assemblies are lower than when they are concealed within a wall.
          </li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">In Wall</p>
      </div>
    ),
  },
  {
    id: 'ylbfNumFusesPerBrace',
    header: 'Number of Simpson Yield Linki Connections per Brace',
    body: (
      <div>
        <ul>
          <li>1 – Simpson Strong-Tie Yield-Link connection at one end of each brace.</li>
          <li>2 – Simpson Strong-Tie Yield-Link connection at both ends of each brace.</li>
        </ul>
        <b>Impact on Risk Assessment:</b>
        <ul>
          <li>Component population</li>
          <li>Global stiffness defaulting</li>
        </ul>
        <b>Default:</b>
        <p className="pl-3">
          Based on seismic design category. Low seismic sites default to one yield link connection
          per brace and high seismic sites default to two yield link connections per brace.
        </p>
      </div>
    ),
  },
  {
    id: 'addDamageStateButton',
    header: 'Maximum Number of Damage States Reached',
    body: <div>A component can have at most 5 damage states.</div>,
  },
  {
    id: 'addConsequenceButton',
    header: 'Maximum Number of Consequences Reached',
    body: (
      <div>
        A component can have at most 5 consequences across all damage states. You must delete a
        consequence somewhere to add a new one here.
      </div>
    ),
  },
  {
    id: 'example1',
    header: 'Example 1',
    body: <div>This is tooltip example 1</div>,
  },
  {
    id: 'example2',
    header: 'Example 2',
    body: <div>This is tooltip example 2</div>,
  },
  {
    id: 'example3',
    header: 'Example 3',
    body: <div>This is tooltip example 3</div>,
  },
  {
    id: 'example4',
    header: 'Example 4',
    body: <div>This is tooltip example 4</div>,
  },
  {
    id: 'example5',
    header: 'Example 5',
    body: <div>This is tooltip example 5</div>,
  },
];

const tooltipsById = keyBy('id', tips);

export default tooltipsById;
